/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { 
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

interface EmailVerificationGuardProps {
  children: React.ReactNode;
  action: string; // Description of what action is being protected
}

/**
 * Component that blocks certain actions for users with unverified emails
 * Shows the children normally for verified users, shows a blocking dialog for unverified users
 */
const EmailVerificationGuard: React.FC<EmailVerificationGuardProps> = ({ children, action }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // If user is verified or not logged in, allow the action
  if (!user || user.isEmailVerified) {
    return <>{children}</>;
  }

  // For unverified users, replace the children with a blocked version
  const handleBlockedClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };

  const handleResendEmail = async () => {
    if (!user?.email) return;
    
    setIsLoading(true);
    try {
      const response = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: user.email }),
      });

      if (response.ok) {
        // Success handled by the dialog close
      } else {
        const errorData = await response.json();
        logger.error('Failed to resend verification email:', errorData);
      }
    } catch (error) {
      logger.error('Error resending verification email:', error);
    } finally {
      setIsLoading(false);
      setOpen(false);
    }
  };

  // Clone children but disable any navigation and add click handler
  const blockedChildren = React.cloneElement(children as React.ReactElement, {
    onClick: handleBlockedClick,
    component: 'div', // Override any RouterLink component
    to: undefined, // Remove any navigation
    href: undefined, // Remove any href
    style: {
      ...((children as React.ReactElement).props.style || {}),
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  });

  return (
    <>
      {blockedChildren}
      
      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BlockIcon color="warning" />
            {t('emailVerificationRequired')}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1">
              {t('needToVerifyEmailBefore', { action })}
            </Typography>
          </Alert>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('sentVerificationLinkTo', { email: user?.email })}
            {' '}
            {t('checkEmailAndClick')}
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('whatYouCanDoUnverified')}
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('viewPublicDebates')} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('browseLeaderboards')} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('exploreTheCube')} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <BlockIcon color="error" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('createOrParticipateDebates')} />
              </ListItem>
            </List>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="secondary">
            {t('close')}
          </Button>
          <Button 
            onClick={handleResendEmail}
            color="primary"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <EmailIcon />}
          >
            {isLoading ? t('sending') : t('resendVerificationEmail')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmailVerificationGuard; 